import { useEffect, useRef } from 'react';
import {
  ClockCircleOutlined,
  CrownOutlined,
  StarOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Divider,
  Flex,
  Grid,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd';
import { useParams } from 'react-router';
import type { Purchase, StudyAccess, Subscription } from '@titularis/api-client';
import { rotuloDoPlano } from '@titularis/ui';
import { api } from '../../api';
import { registrarTela } from '../../analytics';
import { Fato, SecaoDeAjustes, TituloDaPagina, Valor } from '../../components/Ajustes';
import { useAbrirOferta, useCatalogo } from '../../components/Oferta';
import { MiniaturaDoProduto } from '../../components/Produto';
import { produtosAVenda, reais } from '../../comprar';
import { ehLei, nomeDoProduto, produto } from '../../produtos';
import { enderecoDosTermos } from '../../site';
import { contar, dataPorExtenso as data } from '../../texto';

/**
 * Plano: o que você tem agora, e o retorno do pagamento.
 *
 * **Era duas telas até 05/09/2026** - "Assinatura", que mostrava o que a pessoa
 * comprou, e "Planos", uma vitrine com um cartão só. Duas entradas no menu de
 * Configurações para a mesma pergunta, e a segunda existindo apenas para
 * hospedar um botão de compra. Viraram uma, como na Voa: **a tela diz o estado
 * da conta e oferece um caminho; quem vende é a oferta**, que abre por cima e
 * é a mesma de todo o resto do app.
 *
 * Uma tela só também para o retorno do pagamento, e não quatro, porque a
 * pergunta é a mesma nas quatro situações: **o que eu tenho agora?** O que muda
 * é o aviso no topo, e o aviso vem do caminho por onde o provedor devolveu a
 * pessoa.
 *
 * O produto aparece com a **capa**, e não com um ícone de "certo" ao lado do
 * nome. Era esse ícone - o mesmo verde nos quatro produtos - que fazia a tela
 * parecer uma lista de permissões em vez do lugar onde se vê o que se comprou.
 *
 * **O botão de cancelar aparece só para quem tem o que cancelar** (21/09/2026).
 * Até os planos de assinatura ele não existia, e por um motivo bom: no cartão
 * parcelado a compra é uma dívida única junto ao emissor, não há recorrência
 * para interromper, e um botão que não faz nada é pior que nenhum. Isso
 * continua valendo para o semestral e o anual. O que mudou é que agora existe
 * um plano que **de fato** renova, e para ele esconder o botão seria prender a
 * pessoa numa cobrança que ela não consegue parar sozinha.
 */

export function PlanoPage() {
  const { retorno } = useParams<{ retorno?: string }>();
  const telas = Grid.useBreakpoint();
  const { token } = theme.useToken();
  const abrirOferta = useAbrirOferta();

  /*
   * O nome do produto sai do catálogo do servidor, e não escrito aqui: hoje há
   * um só, amanhã haverá mais, e o botão continua dizendo o nome certo sem que
   * ninguém precise lembrar de vir mexer nesta linha.
   */
  const catalogo = useCatalogo();

  useEffect(
    () => registrarTela(`/configuracoes/plano${retorno ? `/${retorno}` : ''}`),
    [retorno],
  );

  /*
   * A volta em que nada foi cobrado reabre a oferta, com o aviso dentro dela.
   *
   * Desde que o checkout nasce do modal, a volta pertence a ele: quem cancelou
   * estava no meio de uma questão, e o provedor o devolve aqui porque a URL de
   * retorno é fixa - mas mostrar um alerta nesta página e deixá-lo procurar de
   * novo o botão que acabou de largar é um passo a mais em quem já desistiu uma
   * vez. Com a oferta aberta, tentar de novo é um clique.
   *
   * O sucesso **não** entra aqui, e desde 17/09/2026 nem chega: quem pagou é
   * levado para `/compra/sucesso`, que é tela própria e ocupa o quadro inteiro.
   */
  const jaAvisou = useRef(false);
  useEffect(() => {
    if (jaAvisou.current) return;
    if (retorno !== 'cancelado' && retorno !== 'expirado') return;
    jaAvisou.current = true;
    abrirOferta({ aviso: retorno });
  }, [retorno, abrirOferta]);

  const status = useQuery({
    queryKey: ['study-status'],
    queryFn: () => api.studyStatus(),
  });

  /*
   * O recibo é outra pergunta que não a do acesso, e por isso é outra consulta:
   * `entitlements` diz o que a conta abre, `orders` diz o que foi pago. Se ela
   * falhar, a tela continua de pé sem a seção - o estado da conta não depende
   * do histórico de pagamento.
   */
  const compras = useQuery({
    queryKey: ['minhas-compras'],
    queryFn: () => api.minhasCompras(),
  });

  const assinaturas = useQuery({
    queryKey: ['minhas-assinaturas'],
    queryFn: () => api.minhasAssinaturas(),
  });

  const acessos = status.data?.acessos ?? [];
  const ativos = acessos.filter((acesso) => acesso.ativo);

  /*
   * O catálogo entra na espera junto com o estado da conta: é dele que sai o
   * que ainda há para vender, e pintar a tela antes dele faria o rodapé da
   * oferta aparecer depois, empurrando o resto - ou não aparecer nunca, se a
   * consulta falhar, deixando o aluno gratuito sem caminho de compra nesta
   * tela.
   */
  if (status.isLoading || catalogo.isLoading || assinaturas.isLoading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  /*
   * A data que interessa é a **mais próxima**: é ela que decide quando a
   * pessoa perde alguma coisa. Prazo nulo é acesso sem fim e fica de fora da
   * conta - senão o "sem prazo" sequestraria o mínimo.
   */
  const proximoFim = ativos
    .map((acesso) => acesso.expiresAt)
    .filter((prazo): prazo is string => prazo !== null)
    .sort()[0];

  /*
   * O que ainda dá para vender, perguntado ao **catálogo do servidor**.
   *
   * Era `Object.keys(PRODUTOS)`, o mapa do bundle, e por isso quem comprava o
   * Enac continuava "faltando" a 1ª e a 2ª fase - que não estão à venda - e lia
   * "Desbloquear o Enac" na própria tela de pagamento confirmado (D-2, G-POLICY).
   * A regra agora é a mesma do `canBuy` do servidor, e mora num lugar só.
   */
  const assinaturasVivas = (assinaturas.data ?? []).filter(
    (assinatura) => assinatura.status === 'ativa',
  );
  /*
   * **Três estados, e não dois.** A primeira versão perguntava só se havia
   * assinatura viva, e quem acabava de cancelar caía no texto da compra única:
   * a tela dizia "não é assinatura e não há o que cancelar" logo abaixo de uma
   * assinatura marcada como cancelada, para alguém que cancelou segundos antes.
   * Quem já assinou continua precisando de uma frase sobre assinatura até o
   * acesso vencer.
   */
  const temAssinaturaViva = assinaturasVivas.length > 0;
  const cancelouEAindaVale =
    !temAssinaturaViva && (assinaturas.data ?? []).length > 0 && ativos.length > 0;
  const aVenda = produtosAVenda(catalogo.data ?? [], assinaturasVivas);
  /*
   * **O que a pessoa ainda não tem vem primeiro** (15/10/2026). Com um produto
   * só, o primeiro do catálogo era o único; com o Enac e a 1ª fase, quem já tem
   * o Enac lia "Desbloquear Enac" e o botão abria a renovação dele - e a 1ª fase
   * só se comprava esbarrando numa questão dela.
   *
   * **Fase que falta pesa mais que lei que falta.** O `grants` do Enac mudou
   * depois das primeiras vendas, e quem comprou antes tem leis diferentes das
   * que ele concede hoje: contar qualquer chave nova como novidade devolvia o
   * Enac a quem o tem. Renovar continua possível - sem nada de novo para somar,
   * a oferta volta a ser o primeiro à venda.
   */
  const jaTem = new Set(ativos.map((acesso) => acesso.product));
  const novas = (produto: (typeof aVenda)[number]) =>
    produto.grants.filter((chave) => !jaTem.has(chave));
  const oferta =
    aVenda.find((produto) => novas(produto).some((chave) => !ehLei(chave))) ??
    aVenda.find((produto) => novas(produto).length > 0) ??
    aVenda[0];

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <TituloDaPagina titulo="Plano" descricao="Gerencie seu acesso e os limites da conta." />

      <SecaoDeAjustes titulo="Plano atual" descricao="A situação da sua conta hoje.">
        <Flex vertical gap={10} style={{ paddingTop: 12 }}>
          {ativos.length === 0 ? (
            <>
              <Fato icone={<CrownOutlined />}>
                Você está no <Valor>plano gratuito</Valor>.
              </Fato>
              {status.data ? (
                <>
                  <Fato icone={<ThunderboltOutlined />}>
                    Ele dá{' '}
                    <Valor>{contar(status.data.limites.freeWeeklyAnswers, 'questão', 'questões')}</Valor>{' '}
                    e{' '}
                    <Valor>
                      {contar(status.data.limites.freeWeeklyCommentaries, 'resolução', 'resoluções')}
                    </Valor>{' '}
                    por semana.
                  </Fato>
                  <Fato icone={<ClockCircleOutlined />}>
                    Nesta semana ainda restam{' '}
                    {contar(status.data.restante.answers, 'questão', 'questões')} e{' '}
                    {contar(status.data.restante.commentaries, 'resolução', 'resoluções')}.
                  </Fato>
                </>
              ) : null}
            </>
          ) : (
            <>
              <Fato icone={<CrownOutlined />}>
                Você tem acesso a{' '}
                {/* Com o rótulo: a chave de uma lei sozinha é um uuid (D-45). */}
                <Valor>
                  {ativos.map((acesso) => nomeDoProduto(acesso.product, acesso.rotulo)).join(', ')}
                </Valor>
                .
              </Fato>
              <Fato icone={<ClockCircleOutlined />}>
                {proximoFim ? (
                  <>
                    {ativos.length > 1 ? 'O primeiro prazo vence' : 'Vale'} até{' '}
                    <Valor>{data(proximoFim)}</Valor>.
                  </>
                ) : (
                  <>
                    Seu acesso é <Valor>sem prazo</Valor>.
                  </>
                )}
              </Fato>
            </>
          )}
        </Flex>

        {/*
          O rodapé com a oferta, na construção da Voa: uma frase à esquerda, o
          botão à direita, um fio separando do estado da conta acima. O botão
          **abre o modal**, e não navega - a tela onde se lê o plano não precisa
          virar a tela onde se compra.
        */}
        {oferta ? (
          <>
            <Divider style={{ marginBlock: 20 }} />
            <Flex align="center" justify="space-between" gap={16} wrap>
              <Flex align="flex-start" gap={10} style={{ flex: 1, minWidth: 240 }}>
                <StarOutlined style={{ color: token.colorTextTertiary, marginTop: 4 }} />
                <Typography.Text type="secondary">
                  {/*
                    Sem artigo antes do nome: ele vem do catálogo, e o
                    primeiro produto feminino ("o 1ª Fase") mostrou que nenhum
                    artigo escrito aqui serve aos dois.
                  */}
                  {ativos.length === 0
                    ? 'Estude sem limite semanal, com a resolução comentada de cada questão do módulo.'
                    : `Ainda dá para somar ${aVenda.map((produto) => produto.name).join(', ')}.`}
                </Typography.Text>
              </Flex>
              {/* O produto vai junto: sem ele, o modal escolheria o primeiro do
                  catálogo, e não o que o botão acabou de nomear. */}
              <Button type="primary" onClick={() => abrirOferta({ produto: oferta.key })}>
                Desbloquear {oferta.name}
              </Button>
            </Flex>
          </>
        ) : null}
      </SecaoDeAjustes>

      {acessos.length > 0 ? (
        <SecaoDeAjustes titulo="Seus produtos" descricao="O que a sua conta abre, e até quando.">
          {acessos.map((acesso, indice) => (
            <ProdutoComprado
              key={acesso.product}
              acesso={acesso}
              primeiro={indice === 0}
              compacta={!telas.sm}
            />
          ))}
        </SecaoDeAjustes>
      ) : null}

      <Assinaturas assinaturas={assinaturas.data ?? []} />

      <Compras compras={compras.data ?? []} />

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        {/*
          Explicar como se cancela é melhor que deixar a pessoa procurar: a
          dúvida "como eu cancelo?" aparece de qualquer forma, e respondê-la
          antes evita um chamado.

          **A frase segue o que a pessoa tem**, e não o que o produto costumava
          ser. Dizer "não há o que cancelar" a quem tem uma mensal viva seria
          falso, e é o tipo de texto que sobrevive à mudança que o tornou
          mentira - por isso ele se decide aqui, e não está escrito fixo.

          O prazo de sete dias entra por ser o que o art. 49 do CDC garante a
          toda compra feita fora de loja física. Ele vale com ou sem este
          parágrafo; escondê-lo só faria a pessoa descobrir pelo Procon.
        */}
        {temAssinaturaViva
          ? 'Sua assinatura renova sozinha até você cancelar, e cancelar não tira o acesso que já está pago - ele vale até a data acima. '
          : cancelouEAindaVale
            ? 'Sua assinatura foi cancelada e não haverá nova cobrança. O acesso que você já pagou continua valendo até a data acima. '
            : 'A compra no cartão é parcelada em uma transação única, junto ao emissor - não é assinatura e não renova sozinha, então não há o que cancelar. '}
        Nos sete primeiros dias você pode desistir e receber tudo de volta, sem
        precisar justificar; depois disso, para pedir reembolso, fale com a
        gente. As condições completas estão nos{' '}
        <Typography.Link href={enderecoDosTermos} target="_blank" rel="noreferrer">
          Termos de Uso
        </Typography.Link>
        .
      </Typography.Paragraph>
    </Space>
  );
}

/**
 * O recibo de cada pagamento.
 *
 * **"Seus produtos" responde o que a conta abre; isto responde o que foi
 * pago** - e são perguntas diferentes, como `entitlements` e `orders` são
 * tabelas diferentes: existe cortesia sem pedido e pedido sem acesso. Até
 * 17/09/2026 a segunda pergunta não tinha resposta em lugar nenhum do app:
 * quem gastou R$ 588,00 em doze vezes não tinha onde conferir o valor, a forma
 * nem o número que o suporte pede.
 *
 * Só o que virou dinheiro aparece. Checkout abandonado e cobrança expirada são
 * ruído de tentativa, não histórico de compra.
 */
const SITUACAO: Partial<Record<Purchase['status'], string>> = {
  pago: 'pago',
  estornado: 'estornado',
  contestado: 'em contestação',
};

/**
 * As cobranças que se repetem, e o único jeito de pará-las.
 *
 * **Cancelada continua na lista**, e não some: a pergunta "eu cancelei mesmo?"
 * aparece dias depois, e uma seção que esvazia responde "não sei" a ela. O que
 * muda é a etiqueta e o sumiço do botão.
 *
 * **O texto da confirmação diz o que NÃO acontece.** O medo de quem clica em
 * cancelar é perder o acesso na hora, e é justamente o que não ocorre - o ciclo
 * pago corre até vencer. Dizer isso antes do clique evita o cancelamento que a
 * pessoa não faz por receio e o chamado de quem achou que tinha perdido tudo.
 */
function Assinaturas({ assinaturas }: { assinaturas: Subscription[] }) {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const cancelar = useMutation({
    mutationFn: (id: string) => api.cancelarAssinatura(id),
    onSuccess: async () => {
      message.success('Assinatura cancelada. O acesso já pago continua valendo.');
      await queryClient.invalidateQueries({ queryKey: ['minhas-assinaturas'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  if (assinaturas.length === 0) return null;

  return (
    <SecaoDeAjustes
      titulo="Assinatura"
      descricao="A cobrança que se repete, e até quando o acesso atual vale."
    >
      {assinaturas.map((assinatura, indice) => (
        <Flex
          key={assinatura.id}
          align="flex-start"
          justify="space-between"
          gap={16}
          wrap
          style={{
            paddingBlock: 14,
            borderTop: indice === 0 ? undefined : `1px solid ${token.colorSplit}`,
          }}
        >
          <Flex vertical style={{ minWidth: 200 }}>
            <Flex align="center" gap={8}>
              <Typography.Text strong>{assinatura.product.name}</Typography.Text>
              <Tag
                color={assinatura.status === 'ativa' ? 'green' : undefined}
                style={{ marginInlineEnd: 0 }}
              >
                {assinatura.status === 'ativa' ? 'ativa' : 'cancelada'}
              </Tag>
            </Flex>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {rotuloDoPlano(assinatura.accessMonths)} · {reais(assinatura.amountCents)}
              {assinatura.status === 'ativa' && assinatura.nextCycleAt
                ? ` · renova em ${data(assinatura.nextCycleAt)}`
                : null}
              {assinatura.status === 'cancelada' && assinatura.canceledAt
                ? ` · cancelada em ${data(assinatura.canceledAt)}`
                : null}
            </Typography.Text>
          </Flex>

          {assinatura.status === 'ativa' ? (
            <Popconfirm
              title="Cancelar a assinatura?"
              description="A próxima cobrança não acontece. O acesso que você já pagou continua valendo até vencer."
              okText="Cancelar assinatura"
              okButtonProps={{ danger: true }}
              cancelText="Manter"
              onConfirm={() => cancelar.mutate(assinatura.id)}
            >
              <Button
                size="small"
                loading={cancelar.isPending && cancelar.variables === assinatura.id}
              >
                Cancelar
              </Button>
            </Popconfirm>
          ) : null}
        </Flex>
      ))}
    </SecaoDeAjustes>
  );
}

function Compras({ compras }: { compras: Purchase[] }) {
  const { token } = theme.useToken();
  const relevantes = compras.filter((compra) => SITUACAO[compra.status]);

  if (relevantes.length === 0) return null;

  return (
    <SecaoDeAjustes titulo="Compras" descricao="O recibo de cada pagamento.">
      {relevantes.map((compra, indice) => (
        <Flex
          key={compra.id}
          align="flex-start"
          justify="space-between"
          gap={16}
          wrap
          style={{
            paddingBlock: 14,
            borderTop: indice === 0 ? undefined : `1px solid ${token.colorSplit}`,
          }}
        >
          <Flex vertical style={{ minWidth: 200 }}>
            <Typography.Text strong>{compra.produto.name}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {data(compra.paidAt ?? compra.createdAt)} ·{' '}
              {compra.forma === 'pix'
                ? 'Pix, à vista'
                : compra.maxInstallments > 1
                  ? `cartão, em até ${compra.maxInstallments}x`
                  : 'cartão, à vista'}
            </Typography.Text>
            {/* O número do pedido é a única coisa daqui que alguém copia. */}
            <Typography.Text
              type="secondary"
              copyable={{ text: compra.id }}
              style={{ fontSize: 11 }}
            >
              Pedido {compra.id}
            </Typography.Text>
          </Flex>

          <Flex vertical align="flex-end" gap={2}>
            <Typography.Text strong>{reais(compra.amountCents)}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {SITUACAO[compra.status]}
            </Typography.Text>
          </Flex>
        </Flex>
      ))}
    </SecaoDeAjustes>
  );
}

/** Uma compra: a capa à esquerda, o prazo no meio, a situação à direita. */
function ProdutoComprado({
  acesso,
  primeiro,
  compacta,
}: {
  acesso: StudyAccess;
  primeiro: boolean;
  compacta: boolean;
}) {
  const { token } = theme.useToken();
  // O rótulo vem do servidor: o nome de uma lei não cabe num mapa do bundle.
  const dele = produto(acesso.product, acesso.rotulo);

  return (
    <Flex
      align="center"
      gap={16}
      style={{
        paddingBlock: 16,
        borderTop: primeiro ? undefined : `1px solid ${token.colorSplit}`,
      }}
    >
      {dele ? (
        <MiniaturaDoProduto
          produto={dele}
          largura={compacta ? 96 : 128}
          apagada={!acesso.ativo}
        />
      ) : null}

      <Flex vertical style={{ flex: 1, minWidth: 0 }}>
        {/*
          Sem `ellipsis`: no celular, "Legislação Comentada" não cabe numa
          linha ao lado da capa e da etiqueta, e cortá-lo em "Legislação
          Comen..." esconde o nome do produto que a pessoa comprou. Quebrar em
          duas linhas custa doze pixels de altura.
        */}
        <Typography.Text strong>{dele?.nome ?? acesso.rotulo}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {acesso.expiresAt
            ? `${acesso.ativo ? 'Vale até' : 'Terminou em'} ${data(acesso.expiresAt)}`
            : 'Sem prazo'}
        </Typography.Text>
      </Flex>

      {acesso.ativo ? (
        <Tag color="green" style={{ marginInlineEnd: 0 }}>
          ativo
        </Tag>
      ) : (
        <Tag style={{ marginInlineEnd: 0 }}>encerrado</Tag>
      )}
    </Flex>
  );
}
